import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Grid,
  Card,
  CardContent,
  CircularProgress,
  Chip,
  Stack,
} from '@mui/material';
import {
  AttachMoney as MoneyIcon,
} from '@mui/icons-material';
import {
  fetchEvents,
  fetchEventCostsSummary,
} from '../../slices/eventsSlice';

const EventsFinanceOverview = ({ events = [] }) => {
  const dispatch = useDispatch();
  const { loading } = useSelector((state) => state.events);
  const [financeData, setFinanceData] = React.useState([]);
  const [summary, setSummary] = React.useState({
    totalEstimated: 0,
    totalActual: 0,
    totalPaid: 0,
    totalRemaining: 0,
  });
  const [loadingSummaries, setLoadingSummaries] = React.useState(true);

  useEffect(() => {
    const loadFinanceData = async () => {
      if (events.length === 0) {
        setLoadingSummaries(false);
        return;
      }

      setLoadingSummaries(true);
      const summaries = [];
      let totalEstimated = 0;
      let totalActual = 0;
      let totalPaid = 0;
      let totalRemaining = 0;

      try {
        // Fetch cost summaries for all events
        const promises = events.map(async (event) => {
          try {
            const result = await dispatch(fetchEventCostsSummary(event.id)).unwrap();
            const eventEstimated = parseFloat(event.estimated_cost || 0);
            const costsEstimated = result.total_estimated || 0;
            const totalEventEstimated = eventEstimated + costsEstimated;
            
            totalEstimated += totalEventEstimated;
            totalActual += result.total_actual || 0;
            totalPaid += result.total_paid || 0;
            totalRemaining += result.remaining || 0;

            return {
              eventId: event.id,
              eventTitle: event.title || 'بدون عنوان',
              eventDate: event.start_date,
              eventEstimatedCost: eventEstimated,
              costsEstimated: costsEstimated,
              totalEstimated: totalEventEstimated,
              totalActual: result.total_actual || 0,
              totalPaid: result.total_paid || 0,
              remaining: result.remaining || 0,
            };
          } catch (error) {
            console.error(`Error fetching summary for event ${event.id}:`, error);
            return null;
          }
        });

        const results = await Promise.all(promises);
        summaries.push(...results.filter(r => r !== null));

        setFinanceData(summaries);
        setSummary({
          totalEstimated,
          totalActual,
          totalPaid,
          totalRemaining,
        });
      } catch (error) {
        console.error('Error loading finance data:', error);
      } finally {
        setLoadingSummaries(false);
      }
    };

    loadFinanceData();
  }, [events, dispatch]);

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB');
  };

  if (loading || loadingSummaries) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Summary Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Stack useFlexGap direction="row" alignItems="center" spacing={2}>
                <MoneyIcon color="primary" />
                <Box>
                  <Typography variant="h6">
                    {summary.totalEstimated.toFixed(2)} ₪
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    إجمالي التكلفة المقدرة
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Stack useFlexGap direction="row" alignItems="center" spacing={2}>
                <MoneyIcon color="info" />
                <Box>
                  <Typography variant="h6">
                    {summary.totalActual.toFixed(2)} ₪
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    إجمالي التكلفة الفعلية
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Stack useFlexGap direction="row" alignItems="center" spacing={2}>
                <MoneyIcon color="success" />
                <Box>
                  <Typography variant="h6" color="success.main">
                    {summary.totalPaid.toFixed(2)} ₪
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    إجمالي المدفوع
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Stack useFlexGap direction="row" alignItems="center" spacing={2}>
                <MoneyIcon color="error" />
                <Box>
                  <Typography variant="h6" color="error.main">
                    {summary.totalRemaining.toFixed(2)} ₪
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    إجمالي المتبقي
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Finance Table */}
      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>الفعالية</TableCell>
                <TableCell>التاريخ</TableCell>
                <TableCell>التكلفة المقدرة للفعالية</TableCell>
                <TableCell>التكلفة المقدرة (تفاصيل)</TableCell>
                <TableCell>إجمالي المقدر</TableCell>
                <TableCell>التكلفة الفعلية</TableCell>
                <TableCell>المدفوع</TableCell>
                <TableCell>المتبقي</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {financeData.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
                      لا توجد بيانات مالية
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                financeData.map((item) => (
                  <TableRow key={item.eventId} hover>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                        {item.eventTitle}
                      </Typography>
                    </TableCell>
                    <TableCell>{formatDate(item.eventDate)}</TableCell>
                    <TableCell>
                      {item.eventEstimatedCost > 0 ? (
                        <Typography variant="body2">
                          {item.eventEstimatedCost.toFixed(2)} ₪
                        </Typography>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          -
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {item.costsEstimated > 0 ? (
                        <Typography variant="body2">
                          {item.costsEstimated.toFixed(2)} ₪
                        </Typography>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          -
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                        {item.totalEstimated.toFixed(2)} ₪
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {item.totalActual > 0 ? (
                        <Typography variant="body2">
                          {item.totalActual.toFixed(2)} ₪
                        </Typography>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          -
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {item.totalPaid > 0 ? (
                        <Chip
                          label={`${item.totalPaid.toFixed(2)} ₪`}
                          color="success"
                          size="small"
                        />
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          -
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {item.remaining > 0 ? (
                        <Chip
                          label={`${item.remaining.toFixed(2)} ₪`}
                          color="error"
                          size="small"
                        />
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          -
                        </Typography>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
};

export default EventsFinanceOverview;
